// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Group, GroupUser, GroupUserSearchFilters, GroupUserState } from '../../types/groups';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './GroupDetail.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const PageFirstIcon = () => (
  <img src="/static/svg/page-first.svg" alt="First page" width="20" height="20" />
);

const PageNextIcon = () => (
  <img src="/static/svg/page-next.svg" alt="Next page" width="20" height="20" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const GroupDetail: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [group, setGroup] = useState<Group | null>(null);
  const [users, setUsers] = useState<GroupUser[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<GroupUserSearchFilters>({
    state: undefined,
  });

  const sessionRole = authService.getSessionRole();

  useEffect(() => {
    if (groupId) {
      loadGroup();
      
      // Initialize filters from URL params
      const cursor = searchParams.get('cursor') || '';
      const state = searchParams.get('state');

      setSearchFilters({
        state: state ? parseInt(state) : undefined,
        cursor: cursor,
      });

      loadUsers(cursor, {
        state: state ? parseInt(state) : undefined,
      });
    }
  }, [groupId]);

  const loadGroup = useCallback(async () => {
    if (!groupId) return;

    setLoading(true);
    setError('');

    try {
      const result = await consoleService.getGroup('', groupId);
      setGroup(result);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load group');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  const loadUsers = useCallback(async (
    cursor?: string,
    filters?: GroupUserSearchFilters
  ) => {
    if (!groupId) return;

    setUsersLoading(true);
    setError('');

    try {
      const result = await consoleService.listGroupUsers(
        '',
        groupId,
        { ...filters, cursor }
      );

      setUsers(result.group_users || []);
      setNextCursor(result.cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load group users');
    } finally {
      setUsersLoading(false);
    }
  }, [groupId]);

  const handleSearch = useCallback((direction: number = 0) => {
    let cursor = '';
    switch (direction) {
      case 0:
        cursor = '';
        break;
      case 1:
        cursor = nextCursor;
        break;
    }

    // Update URL params
    const params = new URLSearchParams();
    if (searchFilters.state !== undefined) params.set('state', searchFilters.state.toString());
    if (cursor) params.set('cursor', cursor);
    
    setSearchParams(params);
    loadUsers(cursor, searchFilters);
  }, [searchFilters, nextCursor, setSearchParams, loadUsers]);

  const handleFilterChange = (field: keyof GroupUserSearchFilters, value: any) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(0);
  };

  const handleRemoveUser = (user: GroupUser, index: number) => {
    openConfirmDialog(
      'Remove User',
      `Are you sure you want to remove "${user.user.username || user.user.id}" from this group? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.removeGroupUsers('', { group_id: groupId!, user_ids: [user.user.id] });
          setUsers(prev => prev.filter((_, i) => i !== index));
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to remove user');
        }
      }
    );
  };

  const handlePromoteUser = async (user: GroupUser, index: number) => {
    try {
      await consoleService.promoteGroupUsers('', { group_id: groupId!, user_ids: [user.user.id] });
      // Reload users to get updated state
      handleSearch(0);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to promote user');
    }
  };

  const handleDemoteUser = async (user: GroupUser, index: number) => {
    try {
      await consoleService.demoteGroupUsers('', { group_id: groupId!, user_ids: [user.user.id] });
      // Reload users to get updated state
      handleSearch(0);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to demote user');
    }
  };

  const handleBanUser = (user: GroupUser, index: number) => {
    openConfirmDialog(
      'Ban User',
      `Are you sure you want to ban "${user.user.username || user.user.id}" from this group? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.banGroupUsers('', { group_id: groupId!, user_ids: [user.user.id] });
          // Reload users to get updated state
          handleSearch(0);
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to ban user');
        }
      }
    );
  };

  const handleKickUser = (user: GroupUser, index: number) => {
    openConfirmDialog(
      'Kick User',
      `Are you sure you want to kick "${user.user.username || user.user.id}" from this group?`,
      async () => {
        try {
          await consoleService.kickGroupUsers('', { group_id: groupId!, user_ids: [user.user.id] });
          setUsers(prev => prev.filter((_, i) => i !== index));
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to kick user');
        }
      }
    );
  };

  const handleBackToGroups = () => {
    navigate('/groups');
  };

  const manageAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getGroupTypeText = (open: boolean) => {
    return open ? 'Open' : 'Private';
  };

  const getUserStateText = (state: number) => {
    switch (state) {
      case GroupUserState.SUPERADMIN: return 'Super Admin';
      case GroupUserState.ADMIN: return 'Admin';
      case GroupUserState.MEMBER: return 'Member';
      case GroupUserState.JOIN_REQUEST: return 'Join Request';
      case GroupUserState.BANNED: return 'Banned';
      default: return 'Unknown';
    }
  };

  const getUserStateBadgeClass = (state: number) => {
    switch (state) {
      case GroupUserState.SUPERADMIN: return 'superadmin';
      case GroupUserState.ADMIN: return 'admin';
      case GroupUserState.MEMBER: return 'member';
      case GroupUserState.JOIN_REQUEST: return 'join-request';
      case GroupUserState.BANNED: return 'banned';
      default: return 'unknown';
    }
  };

  const formatMetadata = (metadata?: string) => {
    if (!metadata) return '';
    try {
      const parsed = JSON.parse(metadata);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return metadata;
    }
  };

  const canPromote = (state: number) => {
    return state === GroupUserState.MEMBER;
  };

  const canDemote = (state: number) => {
    return state === GroupUserState.ADMIN;
  };

  const canBan = (state: number) => {
    return state !== GroupUserState.BANNED && state !== GroupUserState.SUPERADMIN;
  };

  const canKick = (state: number) => {
    return state !== GroupUserState.SUPERADMIN;
  };

  if (loading) {
    return (
      <div className="group-detail-container">
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading group...</strong>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="group-detail-container">
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Group not found</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="group-detail-container">
      <div className="group-detail-header">
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-outline-secondary back-btn"
            onClick={handleBackToGroups}
          >
            ← Back to Groups
          </button>
        </div>
        <h2 className="group-title">{group.name}</h2>
        {group.description && (
          <h6 className="group-subtitle">{group.description}</h6>
        )}
      </div>

      <div className="group-info-section">
        <div className="info-grid">
          <div className="info-item">
            <label>Group ID:</label>
            <code className="group-id">{group.id}</code>
          </div>
          <div className="info-item">
            <label>Type:</label>
            <span className={`type-badge ${group.open ? 'open' : 'private'}`}>
              {getGroupTypeText(group.open)}
            </span>
          </div>
          <div className="info-item">
            <label>Members:</label>
            <div className="member-count">
              <span className="current">{group.edge_count}</span>
              {group.max_count > 0 && (
                <>
                  <span className="separator">/</span>
                  <span className="max">{group.max_count}</span>
                </>
              )}
            </div>
          </div>
          <div className="info-item">
            <label>Language:</label>
            <span>{group.lang_tag || '-'}</span>
          </div>
          <div className="info-item">
            <label>Creator:</label>
            <code className="creator-id">{group.creator_id}</code>
          </div>
          <div className="info-item">
            <label>Created:</label>
            <span>{formatDate(group.create_time)}</span>
          </div>
        </div>

        {group.avatar_url && (
          <div className="info-item full-width">
            <label>Avatar URL:</label>
            <a href={group.avatar_url} target="_blank" rel="noopener noreferrer" className="avatar-link">
              {group.avatar_url}
            </a>
          </div>
        )}

        {group.metadata && (
          <div className="info-item full-width">
            <label>Metadata:</label>
            <pre className="metadata-content">{formatMetadata(group.metadata)}</pre>
          </div>
        )}
      </div>

      <div className="users-section">
        <div className="users-header">
          <h4>Group Members</h4>
          <h6 className="users-subtitle">{users.length} users found.</h6>
        </div>

        <div className="users-controls">
          <div className="search-section">
            <form onSubmit={handleSubmit} className="search-form">
              <div className="search-input-group">
                <select
                  className="state-select"
                  value={searchFilters.state || ''}
                  onChange={(e) => handleFilterChange('state', e.target.value ? parseInt(e.target.value) : undefined)}
                >
                  <option value="">All States</option>
                  <option value={GroupUserState.SUPERADMIN}>Super Admin</option>
                  <option value={GroupUserState.ADMIN}>Admin</option>
                  <option value={GroupUserState.MEMBER}>Member</option>
                  <option value={GroupUserState.JOIN_REQUEST}>Join Request</option>
                  <option value={GroupUserState.BANNED}>Banned</option>
                </select>
                <div className="search-buttons">
                  <button
                    type="submit"
                    className="btn btn-primary search-btn"
                  >
                    Search
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div className="pagination-section">
            <div className="pagination-buttons">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handleSearch(0)}
                disabled={users.length === 0 || usersLoading}
                title="First page"
              >
                <PageFirstIcon />
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => handleSearch(1)}
                disabled={!nextCursor || usersLoading}
                title="Next page"
              >
                <PageNextIcon />
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <RedTriangleIcon />
            <strong>Error:</strong> {error}
          </div>
        )}

        {usersLoading && (
          <div className="alert alert-info">
            <LoadingSpinner />
            <strong className="ms-2">Loading users...</strong>
          </div>
        )}

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Username</th>
                <th>Display Name</th>
                <th>State</th>
                <th>Joined</th>
                {manageAllowed() && <th style={{ width: '200px' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !usersLoading && (
                <tr>
                  <td colSpan={manageAllowed() ? 6 : 5} className="no-users">
                    No users found for this group.
                  </td>
                </tr>
              )}
              {users.map((groupUser, index) => (
                <tr key={`${groupUser.user.id}-${group.id}`} className="user-row">
                  <td>
                    <code className="user-id">{groupUser.user.id}</code>
                  </td>
                  <td>
                    {groupUser.user.username || '-'}
                  </td>
                  <td>
                    {groupUser.user.display_name || '-'}
                  </td>
                  <td>
                    <span className={`state-badge ${getUserStateBadgeClass(groupUser.state)}`}>
                      {getUserStateText(groupUser.state)}
                    </span>
                  </td>
                  <td>
                    {formatDate(groupUser.user.create_time)}
                  </td>
                  {manageAllowed() && (
                    <td className="actions-cell">
                      <div className="action-buttons">
                        {canPromote(groupUser.state) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => handlePromoteUser(groupUser, index)}
                            title="Promote to Admin"
                          >
                            Promote
                          </button>
                        )}
                        {canDemote(groupUser.state) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={() => handleDemoteUser(groupUser, index)}
                            title="Demote to Member"
                          >
                            Demote
                          </button>
                        )}
                        {canBan(groupUser.state) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleBanUser(groupUser, index)}
                            title="Ban User"
                          >
                            Ban
                          </button>
                        )}
                        {canKick(groupUser.state) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleKickUser(groupUser, index)}
                            title="Kick User"
                          >
                            Kick
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default GroupDetail;
